import { skipToken } from "@reduxjs/toolkit/query";
import type { PlaybookCondition } from "@repo/app-types/entities/filterConditions";
import {
  collectAllPlaybookNodes,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import { type FC, useContext, useMemo } from "react";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { RemoveFilterChip } from "./filterChip.js";
import { FilterContext } from "./filterContext.js";
import { checkIsPlaybookHasResultCondition } from "./playbookCondition.js";
import { renderPlaybookCondition } from "./renderPlaybookCondition.js";
import { buildHasResultPlaybookCondition } from "./usePlaybookFilterDefaults.js";

export interface PlaybookConditionsFilterItemProps {
  playbookId: PlaybookId | undefined;
  value: readonly PlaybookCondition[];
  onChange: (newValue: readonly PlaybookCondition[]) => void;
}

export const PlaybookConditionsFilterItem: FC<
  PlaybookConditionsFilterItemProps
> = ({ playbookId, value, onChange }) => {
  const { defaultValues } = useContext(FilterContext);
  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);
  const allPlaybookNodes = useMemo(
    () =>
      playbook ? collectAllPlaybookNodes(playbook.assessment_tree) : undefined,
    [playbook],
  );

  const activeConditions = value.filter(
    (condition) =>
      condition.playbookId === playbookId && !("kind" in condition),
  );

  // The has-result condition is controlled by "Include calls without grades"
  // in the Playbook results menu rather than a per-condition chip. When it's a
  // page default (excluding ungraded calls) and the user has opted back into
  // them, surface a removable chip; removing it re-applies the default.
  const isHasResultCondition = (condition: PlaybookCondition) =>
    condition.playbookId === playbookId &&
    checkIsPlaybookHasResultCondition(condition);
  const includesCallsWithoutGrades =
    playbookId !== undefined &&
    defaultValues.playbookConditions.some(isHasResultCondition) &&
    !value.some(isHasResultCondition);

  return (
    <>
      {activeConditions.map((condition, index) => (
        <RemoveFilterChip
          key={`playbook-condition-${index}`}
          onRemove={() => onChange(value.filter((c) => c !== condition))}
        >
          {renderPlaybookCondition(allPlaybookNodes, condition)}
        </RemoveFilterChip>
      ))}
      {includesCallsWithoutGrades && playbookId !== undefined ? (
        <RemoveFilterChip
          ariaLabel="Grade filter options"
          onRemove={() =>
            onChange([...value, buildHasResultPlaybookCondition(playbookId)])
          }
        >
          Include calls without grades
        </RemoveFilterChip>
      ) : null}
    </>
  );
};
